import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  FailureNotice,
  type HeatStrings,
  heatMessages,
} from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ENERGY_UNITS,
  HEAT_MASS_UNITS,
  nearestSubstance,
  SPECIFIC_HEAT_UNITS,
  tableValueIn,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import { SPECIFIC_HEATS } from "@/lib/tool-kit/chem-physical/heat-data";
import {
  MIX_KEYS,
  type MixKey,
  type MixSolution,
  solveMix,
} from "@/lib/tool-kit/chem-physical/heat-modes";
import {
  TEMPERATURE_UNITS,
  temperatureSymbol,
} from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const CUSTOM = "custom";
const DEFAULT_SIG = 4;
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
type MixText = Record<MixKey, string> & { cal: string };

const START: MixText = {
  m1: "250.0",
  c1: "",
  t1: "310.0",
  m2: "400.0",
  c2: "",
  t2: "22.0",
  tf: "",
  cal: "",
};

function MixAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: MixSolution;
  texts: MixText;
  units: { m: string; c: string; t: string; cal: string };
  sig: number;
  strings: HeatStrings;
}) {
  const fmt = useToolNumber();
  const { unknown, values } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const isTf = unknown === "tf";
  const answerUnit = isTf
    ? temperatureSymbol(units.t)
    : unitSymbol(SPECIFIC_HEAT_UNITS, units.c);
  const answerText = `${formatSig(fmt, solution.answer, sig)} ${answerUnit}`;
  const label = isTf ? strings.mixFinalT : strings.mixSpecificHeat1;
  const substituted = t(
    isTf ? strings.mixSubstitutedTf : strings.mixSubstitutedC1,
    {
      m1: p(values.m1),
      c1: p(values.c1),
      t1: p(values.t1),
      m2: p(values.m2),
      c2: p(values.c2),
      t2: p(values.t2),
      tf: p(values.tf),
      cal: p(solution.cal),
      result: formatSig(fmt, values[unknown], sig + 1),
    }
  );
  const temperatureKeys = (["t1", "t2", "tf"] as const).filter(
    (key) => key !== unknown
  );
  const conversions =
    units.t === "K"
      ? []
      : temperatureKeys.map((key) =>
          t(strings.convertLine, {
            label: { t1: "T₁", t2: "T₂", tf: "T_f" }[key],
            value: texts[key].trim(),
            unit: temperatureSymbol(units.t),
            converted: `${p(values[key])} K`,
          })
        );
  const needsConversion = isTf && units.t !== "K";
  const joules = (value: number) => `${formatSig(fmt, value, sig)} J`;
  const match = isTf ? null : nearestSubstance(values.c1);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={label} value={answerText} />
        <StatCard label={strings.mixQ1} value={joules(solution.q1)} />
        <StatCard
          label={solution.cal > 0 ? strings.mixQ2Cal : strings.mixQ2}
          value={joules(solution.q2 + solution.qCal)}
        />
      </StatGrid>
      {match ? (
        <Notice>
          {t(strings.matchNote, {
            name: strings.substances[
              match.id as keyof typeof strings.substances
            ],
            value: p(match.value),
          })}
        </Notice>
      ) : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.mixBalance}</p>
        <p>{strings.mixEquation}</p>
        <p>{isTf ? strings.mixRearrangedTf : strings.mixRearrangedC1}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{strings.mixUnitsNote}</p>
        <p>{substituted}</p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, {
              result: `${formatSig(fmt, values.tf, sig + 1)} K`,
              answer: answerText,
            })}
          </p>
        ) : null}
        <p>
          {t(strings.mixCheck, {
            q1: joules(solution.q1),
            q2: joules(solution.q2),
            qcal: joules(solution.qCal),
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { label, value: answerText })}
      />
    </>
  );
}

export function HeatMixPanel({
  strings,
  sigChoice,
}: {
  strings: HeatStrings;
  sigChoice: string;
}) {
  const fmt = useToolNumber();
  const [unknown, setUnknown] = useState<"tf" | "c1">("tf");
  const [text, setText] = useState<MixText>(START);
  const [subs, setSubs] = useState({ one: "copper", two: "water" });
  const [unit, setUnit] = useState({ m: "g", c: "JgC", t: "C", cal: "J" });

  const tableText = (id: string) => {
    const value = id === CUSTOM ? null : tableValueIn(id, unit.c);
    return value === null ? null : formatPlain(fmt, value);
  };
  const texts: MixText = {
    ...text,
    c1: tableText(subs.one) ?? text.c1,
    c2: tableText(subs.two) ?? text.c2,
  };
  const outcome = solveMix({ unknown, text: texts, unit });
  const typed = MIX_KEYS.filter((key) =>
    unknown === "tf" ? key !== "tf" : key !== "c1"
  ).map((key) => texts[key]);
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  const labels: Record<string, string> = {
    m1: strings.mixM1,
    c1: strings.mixC1,
    t1: strings.mixT1,
    m2: strings.mixM2,
    c2: strings.mixC2,
    t2: strings.mixT2,
    tf: strings.mixFinalT,
    cal: strings.mixCalorimeter,
  };
  const unitLabels = {
    m: strings.unitMass,
    c: strings.unitSpecificHeat,
    t: strings.unitTemperature,
    cal: strings.unitCalorimeter,
  };
  const unitList = {
    m: unitOptionsOf(HEAT_MASS_UNITS),
    c: unitOptionsOf(SPECIFIC_HEAT_UNITS),
    t: TEMPERATURE_OPTIONS,
    cal: ENERGY_UNITS.slice(0, 4).map((u) => ({
      value: u.id,
      label: `${u.symbol}/°C`,
    })),
  };
  const field = (
    key: MixKey | "cal",
    group: "m" | "c" | "t" | "cal",
    substanceKey?: "one" | "two"
  ) => (
    <QuantityInput
      id={`shm-${key}`}
      key={key}
      label={
        unknown === key
          ? t(strings.answerSuffix, { label: labels[key] })
          : labels[key]
      }
      onUnitChange={(value) => setUnit({ ...unit, [group]: value })}
      onValueChange={(value) => {
        if (substanceKey) {
          setSubs({ ...subs, [substanceKey]: CUSTOM });
        }
        setText({ ...text, [key]: value });
      }}
      solved={unknown === key}
      unit={unit[group]}
      unitLabel={unitLabels[group]}
      units={unitList[group]}
      value={texts[key]}
    />
  );
  const picker = (which: "one" | "two", id: string) => (
    <SelectField
      id={id}
      label={strings.substanceLabel}
      onChange={(value) => setSubs({ ...subs, [which]: value })}
      options={[
        { value: CUSTOM, label: strings.substanceCustom },
        ...SPECIFIC_HEATS.map((entry) => ({
          value: entry.id,
          label:
            strings.substances[entry.id as keyof typeof strings.substances],
        })),
      ]}
      value={subs[which]}
    />
  );
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="shm-solve"
        onChange={setUnknown}
        options={[
          { value: "tf", label: strings.mixFinalT },
          { value: "c1", label: strings.mixSpecificHeat1 },
        ]}
        value={unknown}
      />
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-4">
          <h3 className={WIDGET_H3}>{strings.mixObject1}</h3>
          {unknown === "c1" ? null : picker("one", "shm-sub1")}
          {field("m1", "m")}
          {field("c1", "c", "one")}
          {field("t1", "t")}
        </div>
        <div className="space-y-4">
          <h3 className={WIDGET_H3}>{strings.mixObject2}</h3>
          {picker("two", "shm-sub2")}
          {field("m2", "m")}
          {field("c2", "c", "two")}
          {field("t2", "t")}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {field("cal", "cal")}
        {unknown === "c1" ? field("tf", "t") : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <MixAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={texts}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={heatMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
